import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'

import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { findIconDefinition, IconName, IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
    NavigationAppMenuItem,
    NavigationAppMenuItemIcon,
    NavigationAppMenuItemView,
} from 'features/navigation/types'
import { IconPickerForm } from 'features/workspace/forms/IconPicker'

import { getFaIconName } from 'v2/ui/components/getFaIconName'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Icon } from 'ui/components/Icon'
import { Input } from 'ui/components/Input'
import { Tag } from 'ui/components/Tag'
import { Body } from 'ui/components/Text'
import { Tooltip } from 'ui/components/Tooltip'
import Form from 'ui/deprecated/forms/Form'
import { theme } from 'ui/styling/Theme.css'

import { useNavigationEditorItemState } from './hooks/useNavigationEditorItemState'
import { NAVIGATION_ICON_PACK } from './constants'
import { NavigationEditorItemVisibilityControl } from './NavigationEditorItemVisibilityControl'

import {
    NavigationEditorItemDragHandleStyle,
    NavigationEditorItemOnHoverStyle,
    NavigationEditorItemStyles,
} from './NavigationEditor.css'

type NavigationEditorItemListProps = React.ComponentPropsWithoutRef<typeof Box> & {
    items: NavigationAppMenuItem[]
    isNested?: boolean
    homepageItemSid?: string
    droppableId?: string
    isDragging?: boolean
    isFolder?: boolean
    editingItemId?: string
    setEditingItemId: (id?: string) => void
}

export const NavigationEditorItemList: React.FC<NavigationEditorItemListProps> = ({
    items,
    isNested,
    homepageItemSid,
    droppableId,
    isDragging,
    isFolder,
    editingItemId,
    setEditingItemId,
    ...props
}) => {
    const itemIds = useMemo(() => items.map((item) => item.navigationItemSid), [items])

    if (!items.length && !isFolder) return null

    return (
        <SortableContext id={droppableId} items={itemIds} strategy={verticalListSortingStrategy}>
            <Box
                pointerEvents={isDragging ? 'none' : undefined}
                width="full"
                flex
                column
                {...props}
            >
                {items.map((item) => (
                    <NavigationEditorDndItem
                        key={item.navigationItemSid}
                        item={item}
                        isNested={isNested}
                        homepageItemSid={homepageItemSid}
                        isDragging={isDragging}
                        editingItemId={editingItemId}
                        setEditingItemId={setEditingItemId}
                    />
                ))}
            </Box>
        </SortableContext>
    )
}

type NavigationEditorItemRef = HTMLDivElement

type NavigationEditorItemProps = {
    item: NavigationAppMenuItem
    isNested?: boolean
    homepageItemSid?: string
    dragHandleProps?: Partial<React.ComponentPropsWithoutRef<typeof Icon>>
    style?: React.CSSProperties
    isDragging?: boolean
    isDraggingSelf?: boolean
    editingItemId?: string
    setEditingItemId: (id?: string) => void
}

export const NavigationEditorItem = React.forwardRef<
    NavigationEditorItemRef,
    NavigationEditorItemProps
>(function NavigationEditorItem(
    {
        item,
        isNested,
        homepageItemSid,
        dragHandleProps,
        style,
        isDragging,
        isDraggingSelf,
        editingItemId,
        setEditingItemId,
    },
    ref
) {
    const {
        isEditing,
        onEditClick,
        onCancelClick,
        onSubmit,
        onInputKeyDown,
        isSavingSlow,
        showIcon,
        isHomepage,
        isFolder,
        supportsVisibilityControl,
        supportsDelete,
        isDeleteDisabled,
        onDeleteClick,
        isSeparator,
        isWithinSeparator,
    } = useNavigationEditorItemState({
        item,
        isNested,
        homepageItemSid,
        editingItemId,
        setEditingItemId,
    })

    return (
        <Box
            ref={ref}
            style={style}
            pl={isWithinSeparator ? '5xl' : undefined}
            pt={isSeparator ? 'l' : undefined}
        >
            <Box
                flex
                center
                minHeight="4xl"
                gap="s"
                className={NavigationEditorItemStyles.styleFunction({
                    isDraggingSelf: isDraggingSelf && !isNested,
                    isSeparator,
                })}
            >
                <Box noShrink>
                    <Icon
                        name="GripVertical"
                        size="xs"
                        color="gray300"
                        className={NavigationEditorItemDragHandleStyle}
                        {...dragHandleProps}
                    />
                </Box>
                {isSeparator && (
                    <Icon name="SplitSquareVertical" size="s" color="icon" noShrink ml="2xs" />
                )}
                {isFolder && <Icon name="FolderOpen" size="s" color="icon" noShrink ml="2xs" />}
                {isEditing && (
                    <Box
                        grow
                        shrink
                        as={Form}
                        flex
                        center
                        gap="xs"
                        onSubmit={onSubmit}
                        options={{
                            mode: 'onChange',
                            defaultValues: {
                                icon: (item as NavigationAppMenuItemView).icon?.name,
                                name: item.name,
                            },
                        }}
                    >
                        {showIcon && <IconPicker />}
                        <Box flex center grow shrink>
                            <TitleInput onKeyDown={onInputKeyDown} />
                            <Box flex center gap="2xs">
                                <Button
                                    size="2xs"
                                    variant="ghost"
                                    type="reset"
                                    onClick={onCancelClick}
                                    disabled={isSavingSlow}
                                >
                                    Cancel
                                </Button>
                                <TitleInputSaveButton isLoading={isSavingSlow} />
                            </Box>
                        </Box>
                    </Box>
                )}
                {!isEditing && (
                    <>
                        {showIcon && (
                            <NavigationIcon icon={(item as NavigationAppMenuItemView).icon} />
                        )}
                        <Box flex center gap="xs" grow shrink>
                            <Box trim minWidth={0}>
                                <Body
                                    size="m"
                                    weight={isSeparator ? 'bold' : 'medium'}
                                    color={isSeparator ? 'textWeaker' : 'text'}
                                >
                                    {item.name}
                                </Body>
                            </Box>
                            {isHomepage && (
                                <Tag
                                    type="solid"
                                    color="Neutral"
                                    shade="light"
                                    size="2xs"
                                    pointerEvents="none"
                                    startIcon={{ name: 'Home' }}
                                    noShrink
                                    ml="xs"
                                >
                                    Home
                                </Tag>
                            )}
                            <Button
                                size="2xs"
                                variant="ghost"
                                startIcon={{ name: 'Pencil' }}
                                aria-label="Edit navigation item"
                                className={NavigationEditorItemOnHoverStyle}
                                onClick={onEditClick}
                            />
                        </Box>
                    </>
                )}
                {supportsVisibilityControl && !isDragging && (
                    <Box noShrink>
                        <NavigationEditorItemVisibilityControl
                            item={item}
                            disabled={isSavingSlow}
                        />
                    </Box>
                )}
                {supportsDelete && !isDragging && (
                    <Box noShrink className={NavigationEditorItemOnHoverStyle}>
                        <Tooltip
                            content={
                                isDeleteDisabled ? 'Can only delete empty items' : 'Delete item'
                            }
                            zIndex={9999}
                            asChild={!isDeleteDisabled}
                        >
                            <Button
                                size="xs"
                                variant="ghost"
                                startIcon={{ name: 'X' }}
                                aria-label="Delete item"
                                disabled={isDeleteDisabled}
                                onClick={onDeleteClick}
                            />
                        </Tooltip>
                    </Box>
                )}
            </Box>
            <NavigationEditorItemList
                pl="5xl"
                items={(item as NavigationAppMenuItemView).children ?? []}
                isNested={item.type !== 'folder'}
                homepageItemSid={homepageItemSid}
                droppableId={item.navigationItemSid}
                isDragging={isDragging}
                isFolder={isFolder}
                editingItemId={editingItemId}
                setEditingItemId={setEditingItemId}
            />
        </Box>
    )
})

type NavigationIconProps = {
    icon?: NavigationAppMenuItemIcon
    showEmptyIcon?: boolean
}

export const NavigationIcon: React.FC<NavigationIconProps> = ({ icon, showEmptyIcon }) => {
    if (!icon && showEmptyIcon) {
        return (
            <Box flex center justifyContent="center" height="xl" width="xl" noShrink>
                <Box width="l" height="l" background="gray200" rounded="xs" />
            </Box>
        )
    } else if (!icon) {
        return null
    }

    const iconDef = findIconDefinition({
        prefix: NAVIGATION_ICON_PACK,
        iconName: getFaIconName(icon.name.toString()) as IconName,
    })

    return (
        <Box flex center justifyContent="center" height="xl" width="xl" noShrink>
            {iconDef && (
                <FontAwesomeIcon
                    icon={iconDef}
                    style={{
                        color: theme.color.icon,
                        fontSize: '12px',
                    }}
                />
            )}
        </Box>
    )
}

type IconPickerProps = {}

export const IconPicker: React.FC<IconPickerProps> = () => {
    return (
        <IconPickerForm
            border="none"
            style={{
                width: 'auto',
                overflow: 'hidden',
                height: theme.space['2xl'],
                padding: 0,
            }}
            margin="0"
            name="icon"
            isClearable
            renderCurrentValue={(value?: IconProp) => {
                const icon = value
                    ? { family: 'legacy' as const, name: value as string }
                    : undefined

                return (
                    <Button
                        size="2xs"
                        variant="ghost"
                        endIcon={{ name: 'ChevronDown' }}
                        style={{
                            padding: 0,
                            paddingLeft: theme.space.xs,
                            paddingRight: theme.space.xs,
                        }}
                    >
                        <NavigationIcon icon={icon} showEmptyIcon={true} />
                    </Button>
                )
            }}
            hideArrow
            iconPack={NAVIGATION_ICON_PACK}
        />
    )
}

type TitleInputProps = {
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

export const TitleInput: React.FC<TitleInputProps> = ({ onKeyDown }) => {
    const { register } = useFormContext()

    return (
        <Box>
            <Input
                {...register('name')}
                autoFocus
                size="m"
                variant="borderless"
                autoComplete="off"
                autoCorrect="off"
                onKeyDown={onKeyDown}
                data-1p-ignore
                style={{
                    height: '20px',
                    borderRadius: theme.radius.s,
                    paddingLeft: 0,
                    paddingRight: 0,
                    paddingTop: 0,
                    paddingBottom: 0,
                    backgroundColor: 'transparent',
                }}
            />
        </Box>
    )
}

type TitleInputSaveButtonProps = {
    isLoading: boolean
}

export const TitleInputSaveButton: React.FC<TitleInputSaveButtonProps> = ({ isLoading }) => {
    const { watch } = useFormContext()
    const name = watch('name')
    const isDisabled = !name

    return (
        <Button size="2xs" type="submit" isLoading={isLoading} disabled={isDisabled}>
            Save
        </Button>
    )
}

export const NavigationEditorDndItem: React.FC<NavigationEditorItemProps> = (props) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.item.navigationItemSid,
        transition: {
            duration: 150,
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
        },
    })

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.6 : undefined,
    }

    return (
        <NavigationEditorItem
            {...props}
            ref={setNodeRef}
            style={style}
            dragHandleProps={{ ...attributes, ...listeners }}
            isDraggingSelf={isDragging}
        />
    )
}
